// Labor/wage + markup constants (§5.12 / §5.16). Editable defaults; admin manages
// in Phase 8. Wage RATES are seeded in the DB (migration 0003, CA/NV/Davis-Bacon,
// ID scaffolded) so they can be edited without a deploy. These constants are the
// deterministic glue: category→classification, shift multipliers, default markups.

import type { LaborCategory } from './assemblies';

// BOM labor category → wage classification name (matches seeded labor_rate rows).
export const CATEGORY_CLASSIFICATION: Record<LaborCategory, string> = {
  electrician: 'Electrician (Inside Wireman)',
  sound_comm: 'Sound & Communication Installer',
  technician: 'Low-Voltage Technician',
};

// Off-shift multipliers applied to the base wage (§5.12). Editable in admin.
export type Shift = 'straight' | 'ot' | 'double' | 'swing' | 'graveyard' | 'weekend' | 'holiday';
export const SHIFT_MULTIPLIERS: Record<Shift, number> = {
  straight: 1.0, ot: 1.5, double: 2.0, swing: 1.125, graveyard: 1.15, weekend: 1.5, holiday: 2.0,
};
export const SHIFT_LABELS: Record<Shift, string> = {
  straight: 'Straight time', ot: 'Overtime (1.5×)', double: 'Double time (2×)',
  swing: '2nd / swing shift', graveyard: 'Night / graveyard', weekend: 'Weekend', holiday: 'Holiday',
};

// Pricing hierarchy (§5.11) — lower tier number wins.
export const PRICE_TIER: Record<string, number> = {
  supplier_quote: 1, distributor: 2, manual: 3, internet: 4, historical: 5,
};
export const PRICE_TIER_LABEL: Record<string, string> = {
  supplier_quote: 'Approved supplier quote', distributor: 'Distributor price list',
  manual: 'Admin pricing DB', internet: 'Internet-estimated', historical: 'Historical estimate',
};

// Default project markups (§5.16). Seeded per project on first access; editable.
export interface MarkupDef { name: string; label: string; pct: number; basis: string }
// Defaults calibrated to 3DTSI's won bids (Jun 2026): their bids run ~30–35% gross
// margin (material markup 42–54%, labor margin 31–50%) and 8.75% Sacramento tax.
// Per the GM's direction these targets the CONSERVATIVE end (~30% GM). Loaded labor
// cost already matched their bids (~$54.6/hr), so only the margin side moved.
export const DEFAULT_MARKUPS: MarkupDef[] = [
  { name: 'material_markup', label: 'Material markup', pct: 38, basis: 'pct of material cost' },
  // Soft-cost labor — derived from field labor so it is never forgotten (§5.16).
  { name: 'engineering_design', label: 'Engineering & design', pct: 4, basis: 'pct of labor cost' },
  { name: 'pm_supervision', label: 'Project management & supervision', pct: 8, basis: 'pct of labor cost' },
  { name: 'commissioning', label: 'Programming, testing & commissioning', pct: 5, basis: 'pct of labor cost' },
  { name: 'overhead', label: 'Overhead', pct: 10, basis: 'pct of direct cost' },
  { name: 'profit', label: 'Profit', pct: 20, basis: 'pct of (direct cost + overhead)' },
  { name: 'sales_tax', label: 'Sales tax', pct: 8.75, basis: 'pct of material cost' },
  { name: 'material_escalation', label: 'Material escalation', pct: 0, basis: 'pct of material cost' },
  { name: 'freight', label: 'Freight', pct: 2, basis: 'pct of material cost' },
  { name: 'small_tools', label: 'Small tools & consumables', pct: 1.5, basis: 'pct of labor cost' },
  { name: 'bond', label: 'Bond', pct: 0, basis: 'pct of sell price' },
  { name: 'contingency', label: 'Contingency', pct: 3, basis: 'pct of direct cost' },
];

// State BASE sales-tax rates (%) — the statewide rate; local district taxes add
// more, so these are a starting reference, not the final rate. Editable per project.
export const STATE_SALES_TAX: Record<string, number> = {
  CA: 7.25, NV: 6.85, ID: 6.0, AZ: 5.6, OR: 0, WA: 6.5, UT: 4.85, TX: 6.25,
  FL: 6.0, NY: 4.0, CO: 2.9, NM: 5.0, WY: 4.0, MT: 0,
};

// Selectable travel & misc cost types (§5.13).
export const COST_ADDER_TYPES: { type: string; label: string; unit: string }[] = [
  { type: 'per_diem', label: 'Per diem', unit: 'day' },
  { type: 'hotel', label: 'Hotel', unit: 'night' },
  { type: 'airfare', label: 'Airfare', unit: 'trip' },
  { type: 'rental', label: 'Rental car', unit: 'day' },
  { type: 'parking', label: 'Parking', unit: 'day' },
  { type: 'mileage', label: 'Mileage', unit: 'mile' },
  { type: 'fuel', label: 'Fuel', unit: 'fill' },
  { type: 'travel_time', label: 'Travel time (labor hrs)', unit: 'hour' },
  { type: 'mobilization', label: 'Mobilization', unit: 'ls' },
  { type: 'demobilization', label: 'Demobilization', unit: 'ls' },
  { type: 'lift_rental', label: 'Lift rental (scissor/boom)', unit: 'week' },
  { type: 'permit', label: 'Permit', unit: 'ls' },
  { type: 'engineering', label: 'Engineering / design', unit: 'hour' },
  { type: 'programming', label: 'Programming', unit: 'hour' },
  { type: 'testing', label: 'Testing / commissioning', unit: 'hour' },
  { type: 'training', label: 'Training', unit: 'hour' },
  { type: 'closeout', label: 'Closeout / O&M docs', unit: 'ls' },
];

// ── Industry / facility-type labor productivity factors ──────────────────────
// Multiplier applied to ALL labor hours. High-security, occupied and operational
// environments take far more time for the same install. Factors are grounded in
// standard estimating practice (NECA Manual of Labor Units adjustment factors +
// MCAA labor factors) — each environment stacks "very difficult" conditions:
// work-while-occupied, escorts, extreme safety, constant interruptions, clean/ICRA
// conditions, restricted access windows. Sources: NECA MLU labor-adjustment chart;
// MCAA Labor Factors; FRA Roadway Worker Protection (49 CFR 214); ASHE ICRA 2.0;
// airport SIDA contractor security manuals. Estimators can override per bid.
export interface IndustryFactor { id: string; label: string; factor: number; note: string }

export const INDUSTRY_FACTORS: IndustryFactor[] = [
  { id: 'standard', label: 'Standard commercial / new construction', factor: 1.00, note: 'Baseline — normal access, unoccupied or lightly occupied.' },
  { id: 'school_k12', label: 'K-12 School (occupied)', factor: 1.12, note: 'Background checks (e.g. Live Scan), work around students, summer & after-hours windows.' },
  { id: 'university', label: 'University / college campus', factor: 1.08, note: 'Occupied campus, badging, moderate access limits.' },
  { id: 'hospital', label: 'Hospital / healthcare (occupied)', factor: 1.22, note: 'ICRA infection control, interim life safety, escorts in clinical areas, constant interruptions.' },
  { id: 'data_center', label: 'Data center', factor: 1.15, note: 'Strict access/escort, change-control, clean conditions, zero unplanned outages.' },
  { id: 'airport_airside', label: 'Airport — airside / secured (SIDA)', factor: 1.28, note: 'SIDA badging, security screening, escorts, restricted night work windows, AOA rules.' },
  { id: 'airport_landside', label: 'Airport — landside / terminal', factor: 1.15, note: 'Occupied public terminal, badging, working around travelers.' },
  { id: 'railroad_freight', label: 'Railroad — freight / Class I (BNSF, UP)', factor: 1.30, note: 'Roadway Worker Protection, flagging, limited track windows, e-RAILSAFE, foul time.' },
  { id: 'amtrak', label: 'Amtrak / passenger rail', factor: 1.32, note: 'e-RAILSAFE, electrified catenary (NEC), strict access windows, live passenger operations.' },
  { id: 'transit_rail', label: 'Transit / light rail / subway', factor: 1.28, note: 'Track-access permits, flagging, occupied stations, night windows.' },
  { id: 'prison', label: 'Prison / correctional / detention', factor: 1.38, note: 'Tool control & counts, sally-port escorts, lockdowns, no contraband, constant supervision.' },
  { id: 'military', label: 'Military / federal secure (DoD)', factor: 1.25, note: 'Security clearances, base access/badging, escorts, antiterrorism standards.' },
  { id: 'courthouse', label: 'Courthouse / secure government', factor: 1.15, note: 'Screening, occupied secure areas, restricted hours.' },
];

export const INDUSTRY_BY_ID: Record<string, IndustryFactor> = Object.fromEntries(INDUSTRY_FACTORS.map((x) => [x.id, x]));
